import React, { useRef, useEffect } from 'react';
import { Loader2, RotateCcw, RotateCw } from 'lucide-react';

export default function HtmlChatbot({
    messages,
    inputValue,
    isSending,
    onInputChange,
    onSendMessage,
    onKeyPress,
    allVersions,
    currentVersionIndex,
    onVersionSwitch,
    onUndo,
    onRedo,
    canUndo,
    canRedo
}) {
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    return (
        <div className="flex flex-col h-full">
            {/* History Controls */}
            {allVersions && allVersions.length > 0 && (
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <label className="text-xs font-semibold text-gray-600">Version History</label>
                        <div className="flex gap-1">
                            <button
                                onClick={onUndo}
                                disabled={!canUndo}
                                className={`p-1.5 rounded transition ${canUndo
                                        ? 'text-gray-700 hover:bg-gray-200'
                                        : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Undo"
                            >
                                <RotateCcw className="w-4 h-4" />
                            </button>
                            <button
                                onClick={onRedo}
                                disabled={!canRedo}
                                className={`p-1.5 rounded transition ${canRedo
                                        ? 'text-gray-700 hover:bg-gray-200'
                                        : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Redo"
                            >
                                <RotateCw className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <select
                        value={currentVersionIndex}
                        onChange={(e) => onVersionSwitch(parseInt(e.target.value))}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:border-blue-500"
                    >
                        {allVersions.map((version, index) => (
                            <option key={version.id || index} value={index}>
                                Version {allVersions.length - index} - {new Date(version.created_at).toLocaleString()}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((message) => (
                    <div key={message.id} className="flex gap-2">
                        <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0 ${message.type === 'user' ? 'bg-blue-500' : 'bg-purple-500'
                                }`}
                        >
                            {message.type === 'user' ? 'U' : 'AI'}
                        </div>
                        <div className="flex-1">
                            <div
                                className={`p-3 rounded-lg text-sm ${message.type === 'user'
                                        ? 'bg-blue-50 text-blue-900 border border-blue-200'
                                        : message.isError
                                            ? 'bg-red-50 text-red-900 border border-red-200'
                                            : 'bg-gray-100 text-gray-900 border border-gray-200'
                                    }`}
                            >
                                {message.content}
                            </div>
                            {message.timestamp && (
                                <div className="text-[10px] text-gray-400 mt-1 ml-1">{message.timestamp}</div>
                            )}
                        </div>
                    </div>
                ))}
                {isSending && (
                    <div className="flex gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0 bg-purple-500">
                            AI
                        </div>
                        <div className="flex-1">
                            <div className="p-3 rounded-lg text-sm bg-gray-100 text-gray-900 border border-gray-200 flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Generating HTML...</span>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
                <div className="relative">
                    <textarea
                        value={inputValue}
                        onChange={onInputChange}
                        onKeyPress={onKeyPress}
                        placeholder="Describe your changes..."
                        rows="3"
                        disabled={isSending}
                        className="w-full border border-gray-300 rounded-lg p-3 pr-12 text-sm resize-none focus:outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                    <button
                        onClick={onSendMessage}
                        disabled={!inputValue.trim() || isSending}
                        className="absolute right-2 bottom-2 p-2 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                        onMouseEnter={(e) => {
                            if (!isSending && inputValue.trim()) {
                                e.currentTarget.style.backgroundColor = '#e55a4a';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FF6B5A';
                        }}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
